// The site's service worker, registered by the client (services/app/public/sw.js): the app opens offline and
// installs as an app. The worker's file is beside the client's — at the app root, wherever that is on this host
// (`/` on app.codymccodeface.com, `/app/` on staging: core/assets.js) — so its scope is the app and nothing else:
// on staging the website at `/` is left alone. Not on a Mac's own front (/xr/): there is no worker file there.
// Installing it: Chrome and Edge ask through beforeinstallprompt; Safari on iOS never does, so the way is spelled
// out — Share, then Add to Home Screen — until the app is running from the home screen.
//
// Offline-first (docs/features/offline-first-web.md): the worker answers from this build's cache and offers a new
// build with one bar (offline/update.js); it is registered as `sw.js?build=<this build's id>`.
//
// Seams: `registerOffline(env?)`, called once the page stands (core/live.js, with the install controls), and
// `wireInstall(button, hint, env?)`; `env` stands in for the browser in tests (tools/tests/web-build-pwa.mjs).
import { APP_ROOT, appURL } from './assets.js'
import { servedByMac } from './transport.js'
import { readStamp } from './build-stamp.js'
import { buildId, swURL } from '../offline/update.js'

// (the window's own functions called through arrows: called on another object they throw "Illegal invocation")
const browser = () => ({ navigator: globalThis.navigator, matchMedia: (q) => globalThis.matchMedia?.(q), addEventListener: (t, f) => globalThis.addEventListener?.(t, f), servedByMac: servedByMac(),
  build: buildId(readStamp(globalThis.document?.getElementById?.('build'))) })

export function registerOffline(env = browser()) {
  const sw = env.navigator?.serviceWorker
  if (!sw || env.servedByMac) return
  const scope = APP_ROOT.href
  // under this build's id (offline/update.js `swURL`): a build the browser's own check missed is one it must install
  sw.register(appURL(swURL(env.build || '')), { scope }).catch(() => {})
  // A worker left from when the website and the app shared a host was registered for the whole host; where the
  // app is not the whole host it would go on answering for the website's pages. Only this app's scope stays.
  sw.getRegistrations?.()
    .then((all) => { for (const registration of all) if (registration.scope !== scope) registration.unregister() })
    .catch(() => {})
}

/// Running from the home screen (or as an installed app) already.
export const standalone = (env = browser()) => !!env.matchMedia?.('(display-mode: standalone)').matches || env.navigator?.standalone === true

/// What Safari on an iPhone or iPad is told (it has no install prompt); '' anywhere else, once installed, or on a
/// Mac's own front. An iPad asking for the desktop site says MacIntel with touch points.
export function installHint(env = browser()) {
  const nav = env.navigator || {}
  const iOS = /iPhone|iPad|iPod/.test(nav.userAgent || '') || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)
  return iOS && !standalone(env) && !env.servedByMac ? 'Install on this phone: Share ⎋ → Add to Home Screen.' : ''
}

/// The Install button (shown once the browser offers to install, never when already installed: pressing it asks,
/// and it goes once answered) and the hint under it.
export function wireInstall(button, hint, env = browser()) {
  const installed = standalone(env)
  let prompt = null
  env.addEventListener?.('beforeinstallprompt', (e) => { e.preventDefault(); prompt = e; button.hidden = installed })
  button.addEventListener('click', async () => { if (!prompt) return; prompt.prompt(); await prompt.userChoice; prompt = null; button.hidden = true })
  const text = installHint(env)
  if (text && hint) { hint.textContent = text; hint.hidden = false }
}
